import React, { useEffect, useState } from "react";
import Loader from "../../../components/ui/Loader";
import { MathJaxContext } from "better-react-mathjax";
import apiService from "../../../services/apiService";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { Button as UIButton } from "../../../components/ui";
import QuestionNoStatus from "../question/QuestionNoStatus";

function ViewQSet() {
  const [qName, setQName] = useState("");
  const [loading, setLoading] = useState(false);
  const [qSetId] = useState(useParams().id);
  const [questions, setQuestions] = useState([]);
  const [solutionsMap, setSolutionsMap] = useState(new Map());

  const navigate = useNavigate();

  const config = {
    tex: {
      inlineMath: [
        ["$", "$"],
        ["\\(", "\\)"],
      ],
      displayMath: [
        ["$$", "$$"],
        ["\\[", "\\]"],
      ],
    },
    loader: { load: ["input/tex", "output/chtml"] },
  };

  function buildQueryParams(filter) {
    const params = new URLSearchParams();

    Object.entries(filter || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params.append(key, String(value));
      }
    });

    return params.toString();
  }

  async function fetchQSetName() {
    if (!qSetId) return;

    try {
      const response = await apiService.request(`/i/ims/aiqset/${qSetId}`, {
        method: "GET",
      });

      setQName(response?.title || response?.data?.title || "");
    } catch (err) {
      console.log("Error fetching Question Set Name", err);
    }
  }

  async function fetchQuestions() {
    if (!qSetId) return;

    try {
      setLoading(true);

      const logParams = buildQueryParams({
        qSet: qSetId,
        type: "Question",
      });

      const checkResponse = await apiService.request(
        `/i/ims/ailogs?${logParams}`,
        { method: "GET" },
      );

      if (checkResponse?.[0] && checkResponse[0].savedInServer === 0) {
        window.alert("All Images not saved yet");
        return;
      }

      const qparams = buildQueryParams({
        qSet: qSetId,
        limit: "all",
        sortField: "question_num",
        sortOrder: "ASC",
      });

      const response = await apiService.request(
        `/i/ims/aiquestion?${qparams}`,
        { method: "GET" },
      );

      let questionList = [];

      if (Array.isArray(response?.data)) {
        questionList = response.data;
      } else if (Array.isArray(response)) {
        questionList = response;
      }

      questionList.sort((a, b) => {
        return Number(a.question_num || 0) - Number(b.question_num || 0);
      });

      setQuestions(questionList);
    } catch (err) {
      console.log("Error fetching questions", err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchSolutions() {
    if (!qSetId) return;

    try {
      const logParams = buildQueryParams({
        qSet: qSetId,
        type: "Solution",
      });

      const checkResponse = await apiService.request(
        `/i/ims/ailogs?${logParams}`,
        { method: "GET" },
      );

      if (checkResponse?.[0] && checkResponse[0].savedInServer === 0) {
        window.alert("All Images not saved yet");
        return;
      }

      const sparams = buildQueryParams({
        qSet: qSetId,
        limit: "all",
      });

      const response = await apiService.request(
        `/i/ims/aisolution?${sparams}`,
        { method: "GET" },
      );

      let solutionArray = [];

      if (Array.isArray(response?.data)) {
        solutionArray = response.data;
      } else if (Array.isArray(response)) {
        solutionArray = response;
      }

      const solMap = new Map();

      solutionArray.forEach((solution) => {
        const solutionNo = Number(
          solution.solution_num || solution.question_num || 0,
        );

        if (solutionNo) {
          solMap.set(solutionNo, solution);
        }
      });

      setSolutionsMap(solMap);
    } catch (err) {
      console.log("Error fetching solutions", err);
    }
  }

  function onSave(updatedQues) {
    setQuestions((prev) => {
      if (!Array.isArray(prev)) return prev;

      return prev.map((q) => {
        return q.id === updatedQues._id ? { ...q, ...updatedQues } : q;
      });
    });
  }

  useEffect(() => {
    fetchQSetName();
    fetchQuestions();
    fetchSolutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qSetId]);

  return (
    <MathJaxContext config={config}>
      <div className="p-2 relative">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">View Question Set</h1>

          <UIButton
            onClick={() => navigate("/tools/qSet")}
            variant="light"
            size="sm"
            icon={FaArrowLeft}
          >
            Back to List
          </UIButton>
        </div>

        <div className="flex items-center text-sm mb-6">
          <span className="text-gray-500">Home</span>
          <span className="mx-2">/</span>
          <span
            onClick={() => navigate("/tools/qSet")}
            className="text-gray-500 cursor-pointer"
          >
            QSet
          </span>
          <span className="mx-2">/</span>
          <span className="text-blue-600">View</span>
        </div>

        <div className="flex flex-col bg-white border rounded-md p-2">
          <div className="flex justify-between items-center">
            <div className="p-2 mb-2">
              <label className="font-semibold text-xl">
                Question Set Name:{" "}
              </label>
              <label className="text-xl">{qName}</label>
            </div>

            <div>
              {/* <UIButton
                onClick={() =>
                  window.open(
                    `http://localhost:3000/tools/qSet/compare/${qSetId}`,
                    "_blank",
                    "noreferrer noopener",
                  )
                }
                variant="light"
                size="sm"
              >
                Compare
              </UIButton> */}
              <UIButton
                onClick={() => {
                  const compareUrl = new URL(
                    `/tools/qSet/compare/${qSetId}`,
                    window.location.origin,
                  ).toString();

                  window.open(compareUrl, "_blank", "noopener,noreferrer");
                }}
                variant="light"
                size="sm"
              >
                Compare
              </UIButton>
            </div>
          </div>

          <hr />

          <div className="flex flex-col pt-2">
            {loading ? (
              <Loader />
            ) : Array.isArray(questions) && questions.length > 0 ? (
              questions.map((question, index) => {
                const solution = solutionsMap.get(
                  Number(question.question_num || 0),
                );

                return (
                  <div
                    key={question.id || index}
                    className={`flex justify-between p-3 ${
                      index % 2 === 0
                        ? "hover:bg-gray-100"
                        : "hover:bg-gray-200"
                    }`}
                  >
                    <div className="w-full text-wrap p-1 group">
                      <QuestionNoStatus
                        question={question}
                        qSetId={qSetId}
                        solution={solution}
                        onSave={onSave}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-gray-500">No questions found.</div>
            )}
          </div>
        </div>
      </div>
    </MathJaxContext>
  );
}

export default ViewQSet;

// import React, { useCallback, useEffect, useState } from "react";
// // import axios from "axios";
// import Loader from "../../../components/ui/Loader";
// import { MathJax, MathJaxContext } from "better-react-mathjax";
// // import fileManagerService from "../../../services/fileManagerService";
// import apiService from "../../../services/apiService";
// // import ModalBody from "../../../components/ui/ModalBody";
// // import { Button } from "@material-tailwind/react";
// import { useNavigate, useParams } from "react-router-dom";
// import { FaArrowLeft } from "react-icons/fa";
// import { Button as UIButton } from "../../../components/ui";
// import QuestionNoStatus from "../question/QuestionNoStatus";
// import useAuthStore from "../../../store/authStore";

// function ViewQSet() {
//   const [qName, setQName] = useState("");
//   //   const [uploaderName, setUploaderName] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [qSetId, setqSetId] = useState(useParams().id);
//   const [questions, setQuestions] = useState(null);
//   //const [editingIndex,setEditingIndex]=useState({index:null,ques:null})
//   const [solutionsMap, setSolutionsMap] = useState(new Map());
//   const navigate = useNavigate();
//   const { client } = useAuthStore();
//   //   const serverURL = "http://localhost:1337";

//   const config = {
//     tex: {
//       inlineMath: [["$", "$"]],
//       displayMath: [["$$", "$$"]],
//     },
//     loader: { load: ["input/tex", "output/chtml"] },
//   };

//   useEffect(() => {
//     try {
//       apiService
//         .request(`/i/ims/aiqset/${qSetId}`, {
//           method: "GET",
//         })
//         .then((response) => {
//           setQName(response.title);
//         });
//     } catch (err) {
//       console.log("Error fetching Question Set Name");
//     }
//   }, [qSetId]);

//   useEffect(() => {
//     const fetchData = async () => {
//       let checkResponse;

//       const filter = {
//         qSet: qSetId,
//         type: "Question",
//       };

//       const params = new URLSearchParams();
//       Object.entries(filter).forEach(([key, value]) => {
//         if (value) {
//           params.append(key, value);
//         }
//       });
//       setLoading(true);

//       try {
//         checkResponse = await apiService.request(
//           `/i/ims/ailogs?${params.toString()}`,
//           {
//             method: "GET",
//           },
//         );
//       } catch (err) {
//         console.log("Error fetching Log Status", err);
//         setLoading(false);
//         return;
//       }

//       if (checkResponse[0] && checkResponse[0].savedInServer === 0) {
//         setLoading(false);
//         return window.alert("All Images not saved yet");
//       }

//       let response;
//       const qfilter = {
//         qSet: qSetId,
//         limit: "all",
//         sortField: "question_num",
//         sortOrder: "ASC",
//       };
//       const qparams = new URLSearchParams();
//       Object.entries(qfilter).forEach(([key, value]) => {
//         if (value) {
//           qparams.append(key, value);
//         }
//       });

//       try {
//         response = await apiService.request(
//           `/i/ims/aiquestion?${qparams.toString()}`,
//           {
//             method: "GET",
//           },
//         );
//       } catch (err) {
//         console.log(err);
//         setLoading(false);
//         return;
//       }

//       if (response.data) setQuestions(response.data);
//       else setQuestions(response);

//       setLoading(false);
//       return;
//     };
//     fetchData();
//   }, [qSetId]);

//   useEffect(() => {
//     fetchSolutions();
//   }, [qSetId]);

//   function onSave(updatedQues) {
//     console.log({ updatedQues });
//     setQuestions((prev) => {
//       return prev.map((q) => {
//         return q.id === updatedQues._id ? { ...q, ...updatedQues } : q;
//       });
//     });
//     console.log("Updated Questions: ", questions);
//   }

//   async function fetchSolutions() {
//     if (!qSetId) {
//       return window.alert("No qSet Id provided");
//     }

//     let checkResponse;

//     const filter = {
//       qSet: qSetId,
//       type: "Solution",
//     };

//     const params = new URLSearchParams();
//     Object.entries(filter).forEach(([key, value]) => {
//       if (value) {
//         params.append(key, value);
//       }
//     });

//     try {
//       checkResponse = await apiService.request(
//         `/i/ims/ailogs?${params.toString()}`,
//         {
//           method: "GET",
//         },
//       );
//     } catch (err) {
//       console.log("Error fetching Log Status", err);
//       return;
//     }

//     if (checkResponse[0] && checkResponse[0].savedInServer === 0) {
//       return window.alert("All Images not saved yet");
//     }

//     let response;

//     const sfilter = {
//       qSet: qSetId,
//       limit: "all",
//     };
//     const sparams = new URLSearchParams();
//     Object.entries(sfilter).forEach(([key, value]) => {
//       if (value) {
//         sparams.append(key, value);
//       }
//     });

//     try {
//       response = await apiService.request(
//         `/i/ims/aisolution?${sparams.toString()}`,
//         {
//           method: "GET",
//         },
//       );
//     } catch (err) {
//       console.log(err);
//       return;
//     }

//     if (response.data) {
//       const solutionArray = response.data;
//       const solMap = new Map();
//       solutionArray.forEach((solution) => {
//         solMap.set(solution.solution_num, solution);
//       });
//       setSolutionsMap(solMap);
//     } else {
//       const solutionArray = response;
//       const solMap = new Map();
//       solutionArray.forEach((solution) => {
//         solMap.set(solution.solution_num, solution);
//       });
//       setSolutionsMap(solMap);
//     }
//   }
//   // function getClientS3Dir() {
//   //   try {
//   //     const localClient = JSON.parse(localStorage.getItem("client") || "{}");

//   //     return client?.s3Dir || "435abcschool";
//   //   } catch (error) {
//   //     return client?.s3Dir || "435abcschool";
//   //   }
//   // }
//   function renderTextWithTags(text = "") {
//     if (!text) return "";
//     // const s3Dir = getClientS3Dir();
//     const s3Dir = client?.s3Dir || "";
//     return text.replace(/(image-[\w.-]+\.png)/g, (match, imageName) => {
//       return `<img src="https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${qSetId}/${imageName}" alt="${imageName}" class="inline max-w-full h-auto" />`;
//     });
//   }
//   //   function renderTextWithTags(text) {
//   //     return text.replace(/(image-[\w.-]+\.png)/g, (match, imageName) => {
//   //       return `<img src="https://s3file.s3.us-west-004.backblazeb2.com/1demo/QSet/${qSetId}/${imageName}" alt="${imageName}" class="inline max-w-full h-auto " />`;
//   //     });
//   //     //return text
//   //   }

//   return (
//     <MathJaxContext config={config}>
//       <div className="p-2 relative">
//         <div className="flex justify-between items-center mb-6">
//           <h1 className="text-2xl font-bold">View Question Set</h1>
//           <UIButton
//             onClick={() => navigate("/tools/qSet")}
//             variant="light"
//             size="sm"
//             icon={FaArrowLeft}
//           >
//             Back to List
//           </UIButton>
//         </div>

//         <div className="flex items-center text-sm mb-6">
//           <span className="text-gray-500">Home</span>
//           <span className="mx-2">/</span>
//           <span
//             onClick={() => navigate("/tools/qSet")}
//             className="text-gray-500"
//           >
//             QSet
//           </span>
//           <span className="mx-2">/</span>
//           <span className="text-blue-600">View</span>
//         </div>

//         <div className="flex flex-col bg-white border rounded-md p-2">
//           <div className="flex justify-between items-center">
//             <div className="p-2 mb-2">
//               <label className="font-semibold text-xl">
//                 Question Set Name:{" "}
//               </label>{" "}
//               <label className="text-xl">{qName}</label>
//             </div>

//             <div>
//               <UIButton
//                 onClick={() =>
//                   window.open(
//                     `http://localhost:3000/tools/qSet/compare/${qSetId}`,
//                     "_blank",
//                     "noreferrer noopener",
//                   )
//                 } //navigate(`/tools/qSet/compare/${qSetId}`)}
//                 variant="light"
//                 size="sm"
//               >
//                 Compare
//               </UIButton>
//             </div>
//           </div>

//           <hr />

//           <div className="flex flex-col pt-2">
//             <div>
//               {loading ? (
//                 <Loader />
//               ) : questions && questions.length > 0 ? (
//                 questions.map((question, i) => {
//                   return (
//                     <MathJax key={i}>
//                       <div>
//                         <div
//                           className={`flex justify-between p-3  hover:${i % 2 === 0 ? "bg-gray-100" : "bg-gray-200"}`}
//                         >
//                           <div className="w-full text-wrap p-1 group">
//                             <div>
//                               <QuestionNoStatus
//                                 question={question}
//                                 qSetId={qSetId}
//                                 solution={solutionsMap.get(
//                                   question.question_num,
//                                 )}
//                               />
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </MathJax>
//                   );
//                 })
//               ) : null}
//             </div>
//           </div>
//         </div>
//       </div>
//     </MathJaxContext>
//   );
// }

// export default ViewQSet;
